import { Dialog, Icon, Stack, Text, tokens } from '@/design';
import { PROVIDER_SOURCE_OPTIONS } from './layerModel';

interface ProviderSourcePickerProps {
  open: boolean;
  selectedProviderId: string | null;
  isProviderAllowed(providerId: string): boolean;
  disabledReason(providerId: string): string | null;
  onSelect(providerId: string): void;
  onClose(): void;
}

export function ProviderSourcePicker({
  open,
  selectedProviderId,
  isProviderAllowed,
  disabledReason,
  onSelect,
  onClose,
}: ProviderSourcePickerProps) {
  if (!open) return null;
  return (
    <Dialog title="Provider source" onClose={onClose} size="md">
      <Stack direction="column" gap={2}>
        <Text variant="body" as="p" style={{ margin: 0, color: tokens.color.ink.secondary }}>
          Pick a source provider. Defence-restricted providers stay visible but disabled with reason.
        </Text>
        <div role="listbox" aria-label="Provider source list">
          <Stack direction="column" gap={1}>
            {PROVIDER_SOURCE_OPTIONS.map((option) => {
              const allowed = isProviderAllowed(option.id);
              const reason = disabledReason(option.id);
              const selected = selectedProviderId === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={!allowed}
                  onClick={() => onSelect(option.id)}
                  style={{
                    appearance: 'none',
                    minHeight: tokens.density.touchMin,
                    width: '100%',
                    borderRadius: tokens.radius.input,
                    border: tokens.border.thin,
                    background: selected ? tokens.color.surface[3] : tokens.color.surface[2],
                    color: tokens.color.ink.primary,
                    padding: tokens.space[2],
                    textAlign: 'left',
                    cursor: allowed ? 'pointer' : 'not-allowed',
                    opacity: allowed ? 1 : 0.75,
                  }}
                >
                  <Stack direction="row" align="center" gap={2}>
                    <Icon name={option.icon} size={20} decorative />
                    <Stack direction="column" gap={1} style={{ flex: '1 1 auto' }}>
                      <Text variant="body" as="span">
                        {option.label}
                      </Text>
                      {!allowed && reason ? (
                        <Text variant="caption" as="span" style={{ color: tokens.color.ink.secondary }}>
                          {reason}
                        </Text>
                      ) : null}
                    </Stack>
                    {selected ? <Icon name="check_circle" size={20} decorative /> : null}
                  </Stack>
                </button>
              );
            })}
          </Stack>
        </div>
      </Stack>
    </Dialog>
  );
}
